import { useLazyQuery } from '@apollo/client'
import { useState, useEffect } from 'react'
import { IonContent, IonPage, useIonModal, useIonToast } from '@ionic/react'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import { Helmet } from 'react-helmet'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import styled from 'styled-components'
import Toast from '../../../utils/Toast'
import { LOGIN_PATH, TERMS_PDF_PATH, SETTINGS_PLAN_PATH } from '../../../path'
import { TitleHeader } from '../../../components/Header'
import { DescriptionBox } from '../Component'
import FormFeedback from '../../../components/FormFeedback'
import { redirect_to_checkout } from '../stripe'
import { GET_PAYMENT_SESSION_GQL } from '../../../data/payment'
import { useUser, get_plan } from '../../../data/user'
import { useAuth } from '../../../auth'

const MasterDiv = styled.div`
	padding:20px 26px 0px 26px;

	& .menu {
		display:flex;

		& .menu-items {
			display:flex;
		}

		& .item {
			cursor: pointer;
			border-radius: 30px;
			background-color: #e8e8e8;
			color:#757575;
			margin-right: 12px;
			padding: 6px 10px;
			font-size: 14px;
			font-weight: 400;

			&.selected {	
				background: var(--mu-primary-dark);
				background: -webkit-linear-gradient(to right, var(--mu-primary-dark), var(--mu-primary-light));
				background: linear-gradient(to right, var(--mu-primary-dark), var(--mu-primary-light));
				color:white;
			}
		}
	}

	& .banner {
		background-color: black;
		color:white;
		height: auto;
		border-radius: 12px;
		margin-top: 18px;
		padding:12px;
		display:grid;
		grid-template-rows: 42px auto;
		grid-template-columns: 1fr auto;
		grid-template-areas:
			'title button'
			'text  text';

		& .title {
			grid-area:title;
			color:white;
			font-size:22px;
			padding-bottom:8px;
			text-transform: capitalize;
			font-weight:700;
		}
		& .button {
			grid-area:button;
			color:white;
		}
		& .text {
			grid-area:text;
			color:white;

			& h6 {
				font-size: 14px;
				font-weight: 400;
			}

			& p {
				line-height:15px;
				font-size:12px;
				color:#d5d5d5;
				margin-top: 6px;
			}

			& span, h6 {
				color:white;
			}
		}

		& .upgrade {
			display: flex;
			justify-content: end;
			padding-top: 6px;

			& .upgrade-btn {
				cursor: default;
				height: 25px;
				width: 100px;
				font-size: 12px;
				display: flex;
				align-items: center;
				justify-content: center;
				background-color:white;
				color: black;
				border-radius: 30px;
				font-weight: 500;

				& .upgrade-text {
					display: flex;
					align-items: center;
					justify-content: center;
					margin-left: -5px;
				}

				& svg {
					font-size:20px;
				}
			}
		}
	}

	& .footer {
		height:40px;
	}

	& .floating-footer {
		height:110px;
		background-color:white;
		position: sticky;
		bottom: 0px;
		margin-left: -26px;
		width: calc(100% + 52px);
		display:flex;
		flex-direction: column;
		align-items: center;
		justify-content: start;

		& .buy-button {
			height: 30px;
			min-height: 30px;
			background-color: black;
			color: white;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 14px;
			font-weight: 500;
			width: 310px;
			border-radius: 40px;
			cursor: pointer;

			&.go-to-stripe {
				background-color:#595959;
				color:#949494;
			}
		}

		& .text {
			padding: 16px 26px;
			text-align: center;

			& a {
				cursor:pointer;
				margin-left: 3px;
			}
		}
	}
`

const Menu = ({ ...props }) => {
	const { selected_plan, set_selected_plan, menu_items } = props

	return (<div className="menu">{!menu_items || !menu_items.length || menu_items.length === 1 ? null :
		<div className="menu-items">
			{menu_items.map((item:any) => (
				<div
					key={item.value}
					onClick={() => set_selected_plan(item.value)}
					className={'item' + (selected_plan == item.value ? ' selected' : '')}>
					{item.label}
				</div>
			))}
		</div>}
	</div>)
}

const Plan = () => {
	const authed = useAuth()

	const head = <Helmet>
		<title>Wand - Upgrade Plan</title>
	</Helmet>

	const location = useLocation()
	const searchParams = new URLSearchParams(location.search)
	const plan_param = searchParams.get('plan')

	const [selected_plan, set_selected_plan] = useState(plan_param || 'basic')
	const [menu_items, set_menu_items] = useState<any[]>([])
	const [go_to_stripe, set_go_to_stripe] = useState(false)
	const user_op = useUser({
		onCompleted(data:any) {
			const p = data?.plan?.name || 'free'
			let default_plan = 'basic'
			let items:any[] = []

			if (p == 'free') {
				default_plan = plan_param || 'basic'
				items = [
					{ value: 'organization', label: 'Organisation' },
					{ value: 'professional', label: 'Professional' },
					{ value: 'basic', label: 'Solo' }
				]
			} else if (p == 'basic' || p == 'solo') {
				default_plan = plan_param || 'professional'
				items = [
					{ value: 'organization', label: 'Organisation' },
					{ value: 'professional', label: 'Professional' }
				]
			} else if (p == 'professional') {
				default_plan = plan_param || 'organization'
				items = [
					{ value: 'organization', label: 'Organisation' }
				]
			} else if (p == 'organization') {
				default_plan = ''
				items = []
			}

			set_menu_items(items)
			if (!plan_param) {
				set_selected_plan(default_plan)
			}
		}
	})

	useEffect(() => {
		const params = new URLSearchParams(location.search)
		const plan_from_url = params.get('plan')
		if (plan_from_url && plan_from_url !== selected_plan) {
			set_selected_plan(plan_from_url)
		}
	}, [location.search, selected_plan])

	const toast = new Toast(...useIonToast(), { duration:5000 })
	const [show_org_contact_us, dismiss_org_contact_us] = useIonModal(FormFeedback, {
		mode:'org_upgrade',
		onCancel: () => {
			dismiss_org_contact_us()
		},
		onConfirm: () => {
			toast.show('Request sent')
			dismiss_org_contact_us()
		}
	})

	const theme = useTheme()
	const css_vars:any = {
		'--mu-primary-light': theme.palette.primary.light,
		'--mu-primary-dark': theme.palette.primary.dark,
		'--mu-primary-main': theme.palette.primary.main
	}
	const history = useHistory()
	const redirect = false

	const [get_payment_session] = useLazyQuery(GET_PAYMENT_SESSION_GQL, { notifyOnNetworkStatusChange: true })

	const plan = get_plan(selected_plan)
	const plan_cap = `${selected_plan}`.replace(/^./, x => x.toUpperCase())

	const upgrade = () => {
		if (plan?.name == 'organization') {
			show_org_contact_us()
		} else {
			set_go_to_stripe(true)
			const price_id = selected_plan == 'basic'
				? process.env.REACT_APP_STRIPE_BASIC_PLAN_ID
				: process.env.REACT_APP_STRIPE_PROFESSIONAL_PLAN_ID
			get_payment_session({
				variables:{
					price_id,
					force_stripe_portal: false
				},
				onCompleted(data) {
					const customer_portal_url = data?.payment_session?.customer_portal_url
					const session_id = data?.payment_session?.id

					// If customer_portal_url exists and is a valid URL, redirect to it
					if (customer_portal_url && customer_portal_url.trim() !== '') {
						try {
							new URL(customer_portal_url) // Validate URL
							window.location.href = customer_portal_url
						} catch (e) {
							// If URL is invalid, fall back to checkout session
							redirect_to_checkout(session_id)
						}
					} else {
						// No customer portal URL, use normal checkout flow
						redirect_to_checkout(session_id)
					}
				},
				onError(...args) {
					set_go_to_stripe(false)
					console.log('onError_111')
					console.log(args)
				}
			})
		}
	}

	const current_user_plan = user_op?.data?.plan?.name || 'free'
	const show_trial_text = current_user_plan === 'free' && plan.trial_days

	// Wait for auth check to complete
	if (authed === null) return null

	return redirect ? <Redirect to={LOGIN_PATH} /> : (
		<div>
			<IonPage>
				{head}
				<TitleHeader title="Upgrade Plan" onBack={() => history.push(SETTINGS_PLAN_PATH)} />
				<IonContent fullscreen>
					<div style={css_vars}>{user_op.loading ? null :
						<MasterDiv>
							<Menu selected_plan={selected_plan} set_selected_plan={set_selected_plan} menu_items={menu_items} />
							<div className="banner">
								<Typography variant="h6" component="h2" className="title">
									{selected_plan == 'organization' ? 'organisation' : selected_plan == 'basic' ? 'solo' : selected_plan}
								</Typography>
								<div className="text">
									<Typography variant="h6" color="text.secondary" >
										{plan.price ? `AU$${plan.price}/year + GST` : 'Contact us'}
									</Typography>
									{show_trial_text && (
										<Typography variant="body2" component="p" sx={{ color: '#4caf50', fontWeight: 600, marginTop: '6px' }}>
											Start with {plan.trial_days}-day free trial
										</Typography>
									)}
									<Typography variant="caption" component="p" color="text.secondary" >
										{selected_plan == 'basic'
									? 'Get more towers and more forecast'
									: 'Get more towers, more forecast and more collaboration'}
									</Typography>
								</div>
							</div>
							<DescriptionBox
								title="Features for you"
								items={plan.benefits}
								no_icon_color={true}
							/>
							<div className="footer">
							</div>
							<div className="floating-footer">
								<div
									className={`buy-button${go_to_stripe ? ' go-to-stripe' : ''}`}
									onClick={upgrade}>
									{plan.price ? (show_trial_text ? `Start ${plan.trial_days}-Day Free Trial` : `Get ${plan_cap} for AU$${plan.price}/y + GST`) : 'Contact us'}
								</div>{!go_to_stripe ? null :
								<CircularProgress
									size={24}
									sx={{
										color: 'white',
										position: 'absolute',
										top: '15px',
										left: '50%',
										marginTop: '-12px',
										marginLeft: '-12px',
									}}/>}
								<Typography variant="caption" component="p" className="text" color="text.secondary" >
									{show_trial_text
										? `Start your ${plan.trial_days}-day free trial. After the trial, you'll be charged AU$${plan.price}/year + GST unless you cancel. By proceeding, you agree to the `
										: 'This is a 12-months plan. By proceeding, you have read and agree to the '
									}
									<a target="_blank" rel="noreferrer" href={TERMS_PDF_PATH}>Terms &amp; Conditions</a>.
								</Typography>
							</div>
						</MasterDiv>}
					</div>
				</IonContent>
			</IonPage>
		</div>
	)
}

export default Plan